import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  LuChevronLeft,
  LuChevronRight,
  LuMinus,
  LuPlus,
  LuRotateCcw,
  LuX,
} from 'react-icons/lu';
import '../ComparisonGallery/style.css';
import themeImageSource from '../../../utils/themeImageSource';
import {
  MAX_MODAL_ZOOM as MAX_ZOOM,
  MIN_MODAL_ZOOM as MIN_ZOOM,
  MODAL_MOBILE_QUERY,
  MODAL_ZOOM_STEP as ZOOM_STEP,
  clampModalZoom,
} from '../../../utils/modalImageZoom';

const CLICK_ZOOM = 1.5;
const DRAG_THRESHOLD = 3;

const getModalImageSource = (item, data, isDark) =>
  themeImageSource(item, data.modalImageVariant === 'preview' ? 'preview' : 'full', isDark);

const getDisplayedImageSource = (slide, data, isDark) => {
  const variant = data.modalImageVariant === 'preview' && slide.userZoom === MIN_ZOOM
    ? 'preview'
    : 'full';

  return themeImageSource(slide.item, variant, isDark);
};

const calculateFit = ({
  naturalHeight,
  naturalWidth,
  viewportHeight,
  viewportWidth,
}) => {
  if (!naturalHeight || !naturalWidth || !viewportHeight || !viewportWidth) {
    return null;
  }

  const baseFitScale = Math.min(
    viewportWidth / naturalWidth,
    viewportHeight / naturalHeight
  );

  return {
    baseFitScale,
    fittedWidth: naturalWidth * baseFitScale,
    fittedHeight: naturalHeight * baseFitScale,
  };
};

const ResultGalleryModal = ({
  activeIndex,
  data,
  images,
  isDark,
  isOpen,
  onClose,
  onIndexChange,
  projectId,
}) => {
  const [displayedSlide, setDisplayedSlide] = useState(null);
  const [requestedIndex, setRequestedIndex] = useState(activeIndex);
  const [isLoading, setIsLoading] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const viewportRef = useRef(null);
  const closeButtonRef = useRef(null);
  const requestIdRef = useRef(0);
  const requestedIndexRef = useRef(activeIndex);
  const displayedSlideRef = useRef(null);
  const dimensionsCacheRef = useRef(new Map());
  const preloadedImagesRef = useRef(new Map());
  const pointersRef = useRef(new Map());
  const gestureRef = useRef(null);
  const didDragRef = useRef(false);

  const getViewportSize = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return null;
    return {
      width: viewport.clientWidth,
      height: viewport.clientHeight,
    };
  }, []);

  const setSlide = useCallback((nextSlide) => {
    displayedSlideRef.current = nextSlide;
    setDisplayedSlide(nextSlide);
  }, []);

  const preloadImage = useCallback((src) => {
    if (!src || preloadedImagesRef.current.has(src)) return;

    const image = new Image();
    image.decoding = 'async';
    image.src = src;
    preloadedImagesRef.current.set(src, image);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {
        // The visible image keeps its normal error handling if decoding fails.
      });
    }
  }, []);

  const createPreparedSlide = useCallback((index, dimensions, src) => {
    const viewport = getViewportSize();
    const item = images[index];
    if (!viewport || !item) return null;

    const fit = calculateFit({
      naturalWidth: dimensions.naturalWidth,
      naturalHeight: dimensions.naturalHeight,
      viewportWidth: viewport.width,
      viewportHeight: viewport.height,
    });
    if (!fit) return null;

    return {
      index,
      item,
      src,
      naturalWidth: dimensions.naturalWidth,
      naturalHeight: dimensions.naturalHeight,
      ...fit,
      userZoom: MIN_ZOOM,
      translateX: 0,
      translateY: 0,
    };
  }, [getViewportSize, images]);

  const loadDimensions = useCallback((src, requestId, onReady, onError) => {
    const cached = dimensionsCacheRef.current.get(src);
    if (cached) {
      onReady(cached);
      return;
    }

    const image = new Image();
    image.onload = async () => {
      try {
        if (typeof image.decode === 'function') await image.decode();
      } catch {
        // onload already guarantees usable natural dimensions.
      }
      if (requestId !== requestIdRef.current) return;
      const dimensions = {
        naturalWidth: image.naturalWidth,
        naturalHeight: image.naturalHeight,
      };
      if (!dimensions.naturalWidth || !dimensions.naturalHeight) {
        onError();
        return;
      }
      dimensionsCacheRef.current.set(src, dimensions);
      onReady(dimensions);
    };
    image.onerror = () => {
      if (requestId === requestIdRef.current) onError();
    };
    image.src = src;
  }, []);

  const requestSlide = useCallback((index) => {
    const item = images[index];
    const src = getModalImageSource(item, data, isDark);
    if (!src) return;

    preloadImage(themeImageSource(item, 'full', isDark));

    const requestId = ++requestIdRef.current;
    requestedIndexRef.current = index;
    setRequestedIndex(index);
    setIsLoading(true);
    setHasImageError(false);
    if (displayedSlideRef.current?.src !== src) setSlide(null);

    loadDimensions(
      src,
      requestId,
      (dimensions) => {
        if (requestId !== requestIdRef.current) return;
        const preparedSlide = createPreparedSlide(index, dimensions, src);
        if (!preparedSlide) {
          setIsLoading(false);
          setHasImageError(true);
          return;
        }

        pointersRef.current.clear();
        gestureRef.current = null;
        setIsDragging(false);
        setSlide(preparedSlide);
        setIsLoading(false);
        setHasImageError(false);
        onIndexChange(index);
      },
      () => {
        setIsLoading(false);
        setHasImageError(true);
      }
    );
  }, [createPreparedSlide, data, images, isDark, loadDimensions, onIndexChange, preloadImage, setSlide]);

  const preloadNeighbor = useCallback((index) => {
    const src = getModalImageSource(images[index], data, isDark);
    if (!src || dimensionsCacheRef.current.has(src)) return;

    preloadImage(themeImageSource(images[index], 'full', isDark));

    const image = new Image();
    image.onload = async () => {
      try {
        if (typeof image.decode === 'function') await image.decode();
      } catch {
        // Keep the decoded dimensions from onload.
      }
      if (image.naturalWidth && image.naturalHeight) {
        dimensionsCacheRef.current.set(src, {
          naturalWidth: image.naturalWidth,
          naturalHeight: image.naturalHeight,
        });
      }
    };
    image.src = src;
  }, [data, images, isDark, preloadImage]);

  const updateView = useCallback((updater) => {
    setDisplayedSlide((current) => {
      if (!current) return current;
      const next = updater(current);
      displayedSlideRef.current = next;
      return next;
    });
  }, []);

  const getPanBounds = useCallback((slide, nextZoom) => {
    const viewport = getViewportSize();
    if (!viewport || nextZoom <= MIN_ZOOM) return { x: 0, y: 0 };
    const renderedWidth = slide.fittedWidth * nextZoom;
    const renderedHeight = slide.fittedHeight * nextZoom;
    return {
      x: Math.max(0, renderedWidth - viewport.width) / 2,
      y: Math.max(0, renderedHeight - viewport.height) / 2,
    };
  }, [getViewportSize]);

  const clampPosition = useCallback((slide, position, nextZoom) => {
    const bounds = getPanBounds(slide, nextZoom);
    return {
      x: Math.max(-bounds.x, Math.min(bounds.x, position.x)),
      y: Math.max(-bounds.y, Math.min(bounds.y, position.y)),
    };
  }, [getPanBounds]);

  const changeZoom = useCallback((value) => {
    updateView((current) => {
      const userZoom = clampModalZoom(value);
      const position = userZoom === MIN_ZOOM
        ? { x: 0, y: 0 }
        : clampPosition(current, {
          x: current.translateX,
          y: current.translateY,
        }, userZoom);
      return {
        ...current,
        userZoom,
        translateX: position.x,
        translateY: position.y,
      };
    });
  }, [clampPosition, updateView]);

  const resetView = useCallback(() => {
    updateView((current) => {
      const resetSlide = createPreparedSlide(current.index, {
        naturalWidth: current.naturalWidth,
        naturalHeight: current.naturalHeight,
      }, current.src);

      return resetSlide || {
        ...current,
        userZoom: MIN_ZOOM,
        translateX: 0,
        translateY: 0,
      };
    });
  }, [createPreparedSlide, updateView]);

  const showPrevious = useCallback(() => {
    const index = Math.max(0, requestedIndexRef.current - 1);
    if (index !== requestedIndexRef.current) requestSlide(index);
  }, [requestSlide]);

  const showNext = useCallback(() => {
    const index = Math.min(images.length - 1, requestedIndexRef.current + 1);
    if (index !== requestedIndexRef.current) requestSlide(index);
  }, [images.length, requestSlide]);

  useLayoutEffect(() => {
    if (!isOpen || !viewportRef.current) return undefined;

    const recalculate = () => {
      const current = displayedSlideRef.current;
      if (!current) return;
      const fit = createPreparedSlide(current.index, {
        naturalWidth: current.naturalWidth,
        naturalHeight: current.naturalHeight,
      }, current.src);
      if (!fit) return;
      const position = clampPosition(fit, {
        x: current.translateX,
        y: current.translateY,
      }, current.userZoom);
      setSlide({
        ...fit,
        userZoom: current.userZoom,
        translateX: position.x,
        translateY: position.y,
      });
    };

    recalculate();
    const observer = typeof ResizeObserver === 'function'
      ? new ResizeObserver(recalculate)
      : null;
    observer?.observe(viewportRef.current);
    window.addEventListener('resize', recalculate);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', recalculate);
    };
  }, [clampPosition, createPreparedSlide, isOpen, setSlide]);

  useEffect(() => {
    if (!isOpen) {
      requestIdRef.current += 1;
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => closeButtonRef.current?.focus());

    return () => {
      requestIdRef.current += 1;
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    requestedIndexRef.current = activeIndex;
    setRequestedIndex(activeIndex);
    requestSlide(activeIndex);
  }, [activeIndex, images, isDark, isOpen, requestSlide]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.repeat) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose('keyboard');
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showPrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        showNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, showNext, showPrevious]);

  useEffect(() => {
    if (!isOpen || !displayedSlide) return;
    preloadNeighbor(displayedSlide.index - 1);
    preloadNeighbor(displayedSlide.index + 1);
  }, [displayedSlide, isOpen, preloadNeighbor]);

  const handleWheel = (event) => {
    event.preventDefault();
    const current = displayedSlideRef.current;
    if (!current) return;
    changeZoom(current.userZoom + (event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP));
  };

  const getPointerPair = () => Array.from(pointersRef.current.values()).slice(0, 2);

  const handlePointerDown = (event) => {
    const current = displayedSlideRef.current;
    if (!current) return;
    event.stopPropagation();
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    event.currentTarget.setPointerCapture(event.pointerId);
    didDragRef.current = pointersRef.current.size >= 2;

    if (pointersRef.current.size >= 2) {
      const [first, second] = getPointerPair();
      gestureRef.current = {
        type: 'pinch',
        distance: Math.hypot(second.x - first.x, second.y - first.y),
        zoom: current.userZoom,
      };
      setIsDragging(true);
    } else if (current.userZoom > MIN_ZOOM) {
      gestureRef.current = {
        type: 'pan',
        pointerId: event.pointerId,
        start: { x: event.clientX, y: event.clientY },
        position: { x: current.translateX, y: current.translateY },
      };
      setIsDragging(true);
    } else if (window.matchMedia(MODAL_MOBILE_QUERY).matches) {
      gestureRef.current = {
        type: 'swipe',
        pointerId: event.pointerId,
        start: { x: event.clientX, y: event.clientY },
      };
    }
  };

  const handlePointerMove = (event) => {
    if (!pointersRef.current.has(event.pointerId)) return;
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const gesture = gestureRef.current;
    const current = displayedSlideRef.current;
    if (!gesture || !current) return;

    if (pointersRef.current.size >= 2 && gesture.type === 'pinch') {
      const [first, second] = getPointerPair();
      const distance = Math.hypot(second.x - first.x, second.y - first.y);
      didDragRef.current = true;
      changeZoom(gesture.zoom * (distance / gesture.distance));
      return;
    }

    if (gesture.type === 'pan' && gesture.pointerId === event.pointerId) {
      const deltaX = event.clientX - gesture.start.x;
      const deltaY = event.clientY - gesture.start.y;
      if (Math.abs(deltaX) > DRAG_THRESHOLD || Math.abs(deltaY) > DRAG_THRESHOLD) {
        didDragRef.current = true;
      }
      const position = clampPosition(current, {
        x: gesture.position.x + deltaX,
        y: gesture.position.y + deltaY,
      }, current.userZoom);
      updateView((slide) => ({
        ...slide,
        translateX: position.x,
        translateY: position.y,
      }));
    } else if (gesture.type === 'swipe' && gesture.pointerId === event.pointerId) {
      const deltaX = event.clientX - gesture.start.x;
      const deltaY = event.clientY - gesture.start.y;
      if (Math.abs(deltaX) > DRAG_THRESHOLD && Math.abs(deltaX) > Math.abs(deltaY)) {
        didDragRef.current = true;
      }
    }
  };

  const handlePointerEnd = (event) => {
    const gesture = gestureRef.current;
    if (gesture?.type === 'swipe' && gesture.pointerId === event.pointerId) {
      const deltaX = event.clientX - gesture.start.x;
      const deltaY = event.clientY - gesture.start.y;
      if (Math.abs(deltaX) >= 56 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
        if (deltaX > 0) showPrevious();
        else showNext();
      }
    }
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    pointersRef.current.delete(event.pointerId);
    gestureRef.current = null;
    setIsDragging(false);
  };

  const handleImageClick = (event) => {
    event.stopPropagation();
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    const current = displayedSlideRef.current;
    if (!current) return;
    if (current.userZoom === MIN_ZOOM) changeZoom(CLICK_ZOOM);
    else resetView();
  };

  const displayedIndex = displayedSlide?.index ?? requestedIndex;
  const displayedItem = displayedSlide?.item || images[displayedIndex];
  const isFirstSlide = requestedIndex === 0;
  const isLastSlide = requestedIndex === images.length - 1;
  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          className="comparison-modal result-gallery-modal"
          data-project-id={projectId}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-label={data.modalLabel || data.title}
        >
          <button
            className="comparison-modal__backdrop"
            type="button"
            aria-label={data.closeLabel || 'Закрыть просмотр'}
            onClick={() => onClose('pointer')}
          />
          <motion.div
            className="comparison-modal__panel result-gallery-modal__panel"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <header className="comparison-modal__header">
              <span className="comparison-modal__counter">
                {displayedIndex + 1} / {images.length}
              </span>
              <button
                ref={closeButtonRef}
                className="comparison-modal__close"
                type="button"
                aria-label={data.closeLabel || 'Закрыть просмотр'}
                onClick={() => onClose('pointer')}
              >
                <LuX />
              </button>
            </header>

            <div className="result-gallery-modal__body">
              <div className="result-gallery-modal__content">
                <div className="result-gallery-modal__navigation">
                  <button
                    className="comparison-modal__nav comparison-modal__nav--previous"
                    type="button"
                    aria-label={data.previousLabel || 'Предыдущее изображение'}
                    disabled={isFirstSlide}
                    onClick={showPrevious}
                  >
                    <LuChevronLeft />
                    <span className="comparison-modal__nav-label">{data.previousLabel || 'Назад'}</span>
                  </button>

                  <button
                    className="comparison-modal__nav comparison-modal__nav--next"
                    type="button"
                    aria-label={data.nextLabel || 'Следующее изображение'}
                    disabled={isLastSlide}
                    onClick={showNext}
                  >
                    <span className="comparison-modal__nav-label">{data.nextLabel || 'Вперёд'}</span>
                    <LuChevronRight />
                  </button>
                </div>

                <div
                  ref={viewportRef}
                  className={`comparison-modal__detail result-gallery-modal__viewport${displayedSlide?.userZoom > 1 ? ' is-zoomed' : ''}${isDragging ? ' is-dragging' : ''}`}
                  onWheel={handleWheel}
                >
                  {isLoading ? (
                    <span className="comparison-modal__loading result-gallery-modal__loading">
                      {data.loadingLabel || 'Загрузка изображения…'}
                    </span>
                  ) : null}
                  {hasImageError ? (
                    <p className="comparison-modal__error result-gallery-modal__error">
                      {data.errorLabel || 'Не удалось загрузить изображение'}
                    </p>
                  ) : null}
                  <div className="comparison-modal__image-stage result-gallery-modal__image-stage">
                    {displayedSlide ? (
                      <img
                        className="result-gallery-modal__image"
                        src={getDisplayedImageSource(displayedSlide, data, isDark)}
                        alt={displayedSlide.item.alt}
                        data-base-fit-scale={displayedSlide.baseFitScale}
                        data-user-zoom={displayedSlide.userZoom}
                        data-pan-x={displayedSlide.translateX}
                        data-pan-y={displayedSlide.translateY}
                        width={displayedSlide.naturalWidth}
                        height={displayedSlide.naturalHeight}
                        decoding="async"
                        draggable="false"
                        onClick={handleImageClick}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerEnd}
                        onPointerCancel={handlePointerEnd}
                        style={{
                          width: displayedSlide.fittedWidth * displayedSlide.userZoom,
                          height: displayedSlide.fittedHeight * displayedSlide.userZoom,
                          marginLeft: -(displayedSlide.fittedWidth * displayedSlide.userZoom) / 2,
                          marginTop: -(displayedSlide.fittedHeight * displayedSlide.userZoom) / 2,
                          transform: displayedSlide.translateX || displayedSlide.translateY
                            ? `translate3d(${displayedSlide.translateX}px, ${displayedSlide.translateY}px, 0)`
                            : 'none',
                        }}
                      />
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="result-gallery-modal__footer">
                {displayedItem?.title || displayedItem?.description ? (
                  <div className="comparison-modal__copy">
                    {displayedItem.title ? <strong>{displayedItem.title}</strong> : null}
                    {displayedItem.description ? <p>{displayedItem.description}</p> : null}
                  </div>
                ) : null}
                <div className="result-gallery-modal__zoom-controls" aria-label={data.zoomLabel || 'Масштаб изображения'}>
                  <button
                    type="button"
                    aria-label={data.zoomOutLabel || 'Уменьшить изображение'}
                    disabled={!displayedSlide || displayedSlide.userZoom <= MIN_ZOOM}
                    onClick={() => changeZoom(displayedSlide.userZoom - ZOOM_STEP)}
                  >
                    <LuMinus />
                  </button>
                  <button
                    type="button"
                    aria-label={data.resetZoomLabel || 'Сбросить масштаб'}
                    disabled={!displayedSlide || displayedSlide.userZoom === MIN_ZOOM}
                    onClick={resetView}
                  >
                    <LuRotateCcw />
                    <span>{Math.round((displayedSlide?.userZoom || MIN_ZOOM) * 100)}%</span>
                  </button>
                  <button
                    type="button"
                    aria-label={data.zoomInLabel || 'Увеличить изображение'}
                    disabled={!displayedSlide || displayedSlide.userZoom >= MAX_ZOOM}
                    onClick={() => changeZoom(displayedSlide.userZoom + ZOOM_STEP)}
                  >
                    <LuPlus />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export default ResultGalleryModal;
